// Per-user interface preferences: column widths, column visibility, density,
// saved views, dismissed hint bars.
//
// 06-qol §3 says these are "persisted per user" — but users arrive in S3/S4, and
// this slice has none. So the seam is here from the start: everything goes through
// `PreferenceStore`, and today the only implementation is browser storage keyed by
// the viewer's id. When S4 lands, an API-backed store drops in behind the same
// interface and nothing that reads a preference changes.
//
// Storage is namespaced by user id rather than shared, so two people on one machine
// never inherit each other's column layout — and so the future migration is a copy
// of one namespace rather than a guess about who owned what.

import { PREFERENCE_KEY_PATTERN, type PreferenceValues } from '@nest-recruit/shared';
import { useToasts } from '../stores/toasts';
import { apiDelete, apiGet, apiPut } from './api-client';

export interface PreferenceStore {
  get<TValue>(key: string, fallback: TValue): TValue;
  set<TValue>(key: string, value: TValue): void;
  remove(key: string): void;
}

/** Density is one setting for the whole app, not per screen (06-qol §3). */
export const DENSITY_KEY = 'table.density';

/** Column widths, visibility and sort are per screen — hence the screen id. */
export const columnWidthsKey = (screenId: string): string => `table.${screenId}.widths`;
export const columnVisibilityKey = (screenId: string): string => `table.${screenId}.columns`;
export const savedViewsKey = (screenId: string): string => `table.${screenId}.views`;

/** The command palette's Recent section (06-qol §1: last 10 records viewed). */
export const RECENT_RECORDS_KEY = 'palette.recent';

/** Theme is read by the boot script in index.html before React exists, so it is
 *  deliberately NOT namespaced by user — nobody is signed in yet at that point. */
export const THEME_STORAGE_KEY = 'nr.theme';

const NAMESPACE = 'nr';

/**
 * Browser-backed store.
 *
 * Every access is wrapped: `localStorage` is not merely empty in a private window
 * or with site data blocked — the accessor itself throws. A preference is a
 * convenience, so a store that cannot read must degrade to the default rather than
 * take a screen down with it.
 */
export function createLocalPreferenceStore(userId: string): PreferenceStore {
  const prefix = `${NAMESPACE}.${userId}.`;

  return {
    get<TValue>(key: string, fallback: TValue): TValue {
      try {
        const raw = window.localStorage.getItem(prefix + key);
        if (raw === null) return fallback;
        return JSON.parse(raw) as TValue;
      } catch {
        // Unreadable or corrupt. The default is always a correct answer.
        return fallback;
      }
    },

    set<TValue>(key: string, value: TValue): void {
      try {
        window.localStorage.setItem(prefix + key, JSON.stringify(value));
      } catch {
        // Full, or storage is denied. Losing a column width is not worth an error.
      }
    },

    remove(key: string): void {
      try {
        window.localStorage.removeItem(prefix + key);
      } catch {
        // As above.
      }
    },
  };
}

/** For tests, and for any surface that must not persist — impersonation (S3). */
export function createMemoryPreferenceStore(): PreferenceStore {
  const values = new Map<string, unknown>();
  return {
    get<TValue>(key: string, fallback: TValue): TValue {
      return values.has(key) ? (values.get(key) as TValue) : fallback;
    },
    set<TValue>(key: string, value: TValue): void {
      values.set(key, value);
    },
    remove(key: string): void {
      values.delete(key);
    },
  };
}

// --- S32 (DECISIONS-LOG S29–S32 Q26): the server keeps them, so they follow the person ----------

/** Unsent changes per key, so a column drag is one write when it settles, not forty. */
const SYNC_DELAY_MS = 600;
const pendingWrites = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * The store every screen uses: the browser copy answers at once, and each change is also sent
 * to the server, which gives it back on any other device at the next sign-in. A key the server
 * would refuse stays in the browser only.
 */
export function createUserPreferenceStore(userId: string): PreferenceStore {
  const local = createLocalPreferenceStore(userId);
  const isSynced = userId !== 'anonymous';
  return {
    get: (key, fallback) => local.get(key, fallback),
    set<TValue>(key: string, value: TValue): void {
      local.set(key, value);
      if (isSynced && PREFERENCE_KEY_PATTERN.test(key)) schedule(key, () => apiPut<void>(`/me/preferences/${key}`, { value }));
    },
    remove(key: string): void {
      local.remove(key);
      if (isSynced && PREFERENCE_KEY_PATTERN.test(key)) schedule(key, () => apiDelete<void>(`/me/preferences/${key}`));
    },
  };
}

function schedule(key: string, send: () => Promise<void>): void {
  const waiting = pendingWrites.get(key);
  if (waiting) clearTimeout(waiting);
  pendingWrites.set(
    key,
    setTimeout(() => {
      pendingWrites.delete(key);
      send().catch(() => {
        // The browser copy already holds the change, and the next change sends it again.
        useToasts.getState().show({ tone: 'warning', message: "Your layout wasn't saved to your account", detail: 'It is kept on this device and goes with your next change.' });
      });
    }, SYNC_DELAY_MS),
  );
}

/** At sign-in: the server's copy wins over whatever this browser remembered. */
export async function loadServerPreferences(userId: string): Promise<PreferenceValues> {
  const values = await apiGet<PreferenceValues>('/me/preferences');
  const local = createLocalPreferenceStore(userId);
  for (const [key, value] of Object.entries(values)) local.set(key, value);
  return values;
}
